import {
  ColDef,
  ICellRendererParams,
  RowDragEndEvent,
} from 'ag-grid-enterprise';
import { Bell, Search, SlidersHorizontal, X, Zap } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge } from 'src/components/Badge';
import Button from 'src/components/Button';
import AGGrid from 'src/components/table/AGGrid';
import BaseModal from 'src/components/BaseModal';
import {
  adminInputClassName,
  configCardClassName,
  configContainerClassName,
} from 'src/pages/admin/utils';
import { RuleComposer } from 'src/components/checklist/rule/RuleComposer';
import { IsValidRule } from 'src/utils/rule/rule-evaluator';
import {
  extractBracketedValues,
  buildRuleBuilderJson,
  rebuildExpressionWithLatestDisplayNames,
  buildNameExpression,
} from 'src/utils/rule/rule-field-extract';
import { useExternalFields } from 'src/queries/fields/useExternalFields';
import { ExternalFieldType } from 'src/services/fields/enums';
import { useDocumentTypes } from 'src/queries/documents/useDocumentTypes';
import { useEncompassFieldSchema } from 'src/queries/encompass/useEncompassFieldSchema';
import { UpdateLosFieldEditor } from 'src/components/checklist/rule/UpdateLosFieldEditor';
import { EncompassFieldConditionEditor } from 'src/components/checklist/rule/EncompassFieldConditionEditor';
import { EncompassFieldTriggerEditor } from 'src/components/checklist/rule/EncompassFieldTriggerEditor';
import { getUpdateLosFieldError } from 'src/components/checklist/rule/updateLosFieldValidation';
import { hasValidEncompassFieldConditions } from 'src/utils/rule/encompassFieldConditions';
import {
  RuleSettingsTabs,
  RuleTab,
} from 'src/components/checklist/rule/RuleSettingsTabs';
import { RuleVersionType } from 'src/services/documents/enums';
import {
  LoanChecklistRule,
  LoanChecklistRuleCondition,
  LoanChecklistRuleDefinition,
} from 'src/services/loans/types';
import { useChecklistStore } from 'src/stores/useChecklistStore';
import { ChecklistSyncButton } from './ChecklistSyncButton';
import { ChecklistSyncStatus } from './ChecklistSyncStatus';
import { TrueToggle } from '../TrueToggle';
import { cn } from 'src/utils/cn';
import { useNavigate, useParams } from 'react-router';
import { useTenantChecklistById } from 'src/queries/checklist/useTenantChecklistById';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useRuleUpdateByIdMutation } from 'src/queries/checklist/useRuleUpdateMutation';
import { toast } from 'react-toastify';
import { useSaveTenantChecklistMutation } from 'src/queries/checklist/useSaveTenantChecklist';
import { createId } from '@paralleldrive/cuid2';
import { useConditionTypesQuery } from 'src/queries/encompass/useConditionTypesQuery';
import { usePriorToDefinitionsQuery } from 'src/queries/encompass/usePriorToDefinitionsQuery';
import {
  getEnabledEnhancedConditionDefaults,
  getEnabledStandardConditionDefaults,
  getNewRuleConditionDefaults,
  isStandardConditionRule,
  STANDARD_CONDITION_TYPES,
  withPendingStandardConditionRemoval,
} from 'src/utils/rule/conditionDefaults';
import {
  normalizeRulesBySequence,
  resequenceRules,
  sortRulesBySequence,
} from 'src/utils/checklistOrdering';

export type RuleRow = LoanChecklistRule & { localId: string };

const buildRuleRow = (rule: LoanChecklistRule, index: number): RuleRow => ({
  ...rule,
  localId: rule.id ?? `rule-${index}-${createId().toString().slice(2, 10)}`,
});

const createEmptyDefinition = (): LoanChecklistRuleDefinition => ({
  ruleType: '',
  ruleFunction: '',
  ruleSequenceNo: 0,
  active: true,
  expression: '',
  trueConstraint: '',
  falseConstraint: '',
  loanTaskOnFailure: false,
  ...getNewRuleConditionDefaults(),
  draftRule: false,
  version: RuleVersionType.FINAL,
  ruleBuilderJson: [],
  nameExpression: '',
  borrowerPairId: 'COMMON',
});

const createNewRule = (index: number): RuleRow => ({
  localId: `new-rule-${Date.now()}-${index}`,
  name: '',
  description: '',
  definition: createEmptyDefinition(),
  userTaskId: '',
});

const RuleExpressionRenderer = ({ data }: ICellRendererParams<RuleRow>) => (
  <span title={data?.definition.expression || ''}>
    {data?.definition.expression || '-'}
  </span>
);

const RuleActionRenderer = ({
  data,
  onEdit,
}: ICellRendererParams<RuleRow> & {
  onEdit?: (rule: RuleRow) => void;
}) => (
  <div className="flex size-full items-center gap-2 justify-center">
    <Button
      variant="contained"
      className="gap-2 py-1 px-2 text-xs rounded-sm"
      onClick={() => data && onEdit?.(data)}
    >
      Edit
    </Button>
  </div>
);

interface RuleFormModalProps {
  onCancel: () => void;
  onSave: (rule: RuleRow) => void;
}

type ConditionSelectOption = {
  id: string;
  title: string;
};

type PriorToOption = {
  name: string;
};

type RuleConditionPushSettingsProps = {
  definition: LoanChecklistRuleDefinition;
  standardConditionEnabled: boolean;
  enhancedConditionEnabled: boolean;
  conditionTypeId?: string;
  conditionTypes: ConditionSelectOption[];
  priorToOptions: PriorToOption[];
  isPriorToLoading: boolean;
  isPriorToError: boolean;
  isEnhancedConditionIncomplete: boolean;
  isStandardConditionIncomplete: boolean;
  onEnhancedConditionPushToggle: () => void;
  onStandardConditionPushToggle: () => void;
  onConditionTypeChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  onStandardConditionTypeChange: (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => void;
  onPriorToChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
};

function RuleConditionPushSettings({
  definition,
  standardConditionEnabled,
  enhancedConditionEnabled,
  conditionTypeId,
  conditionTypes,
  priorToOptions,
  isPriorToLoading,
  isPriorToError,
  isEnhancedConditionIncomplete,
  isStandardConditionIncomplete,
  onEnhancedConditionPushToggle,
  onStandardConditionPushToggle,
  onConditionTypeChange,
  onStandardConditionTypeChange,
  onPriorToChange,
}: Readonly<RuleConditionPushSettingsProps>) {
  return (
    <>
      <div className="flex flex-col gap-2">
        <div
          className={cn(
            'flex flex-row items-center justify-between rounded border p-2.5',
            enhancedConditionEnabled
              ? 'border-blue-200 bg-blue-50/30'
              : 'border-gray-200',
          )}
        >
          <div className="flex flex-col">
            <span className="text-sm font-medium text-gray-700">
              Enhanced Condition Push
            </span>
            <span className="text-[10px] text-gray-500">
              Configure for enhanced loans
            </span>
          </div>
          <button
            type="button"
            role="switch"
            aria-label="Enhanced condition push"
            aria-checked={enhancedConditionEnabled}
            className={cn(
              'relative inline-flex h-4 w-7 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none',
              enhancedConditionEnabled ? 'bg-blue-600' : 'bg-gray-200',
            )}
            onClick={onEnhancedConditionPushToggle}
          >
            <span
              aria-hidden="true"
              className={cn(
                'pointer-events-none inline-block h-3 w-3 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                enhancedConditionEnabled ? 'translate-x-3' : 'translate-x-0',
              )}
            />
          </button>
        </div>
        {enhancedConditionEnabled && (
          <div className="ml-3 flex flex-col gap-3 border-l-2 border-blue-200 pl-3">
            <div className="flex flex-col gap-1">
              <label
                htmlFor="admin-condition-type"
                className="block text-xs font-medium text-gray-700"
              >
                Condition Type
              </label>
              <select
                id="admin-condition-type"
                value={definition.conditionTypeId ?? ''}
                onChange={onConditionTypeChange}
                className="block w-full rounded border border-gray-300 py-1 pl-2 pr-6 text-gray-700 shadow-sm outline-none focus:ring-0 sm:text-xs"
              >
                <option value="">Select condition type</option>
                {conditionTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.title}
                  </option>
                ))}
              </select>
              {isEnhancedConditionIncomplete && !definition.conditionTypeId && (
                <p className="text-xs text-red-600">
                  Condition type is required.
                </p>
              )}
            </div>
            <div className="flex flex-col gap-1">
              <label
                htmlFor="admin-condition-prior-to"
                className="block text-xs font-medium text-gray-700"
              >
                Prior To
              </label>
              <select
                id="admin-condition-prior-to"
                value={definition.priorTo ?? ''}
                onChange={onPriorToChange}
                disabled={!conditionTypeId || isPriorToLoading}
                className="block w-full rounded border border-gray-300 py-1 pl-2 pr-6 text-gray-700 shadow-sm outline-none focus:ring-0 disabled:bg-gray-100 disabled:text-gray-400 sm:text-xs"
              >
                <option value="">
                  {isPriorToLoading ? 'Loading...' : 'Select prior to'}
                </option>
                {priorToOptions.map((option) => (
                  <option key={option.name} value={option.name}>
                    {option.name}
                  </option>
                ))}
              </select>
              {isPriorToError && (
                <p className="text-xs text-red-600">
                  Failed to load Prior To options.
                </p>
              )}
              {isEnhancedConditionIncomplete && !definition.priorTo && (
                <p className="text-xs text-red-600">Prior To is required.</p>
              )}
            </div>
          </div>
        )}
      </div>
      <div className="flex flex-col gap-2">
        <div
          className={cn(
            'flex flex-row items-center justify-between rounded border p-2.5',
            standardConditionEnabled
              ? 'border-blue-200 bg-blue-50/30'
              : 'border-gray-200',
          )}
        >
          <div className="flex flex-col">
            <span className="text-sm font-medium text-gray-700">
              Standard Condition Push
            </span>
            <span className="text-[10px] text-gray-500">
              Configure for standard loans
            </span>
          </div>
          <button
            type="button"
            role="switch"
            aria-label="Standard condition push"
            aria-checked={standardConditionEnabled}
            className={cn(
              'relative inline-flex h-4 w-7 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none',
              standardConditionEnabled ? 'bg-blue-600' : 'bg-gray-200',
            )}
            onClick={onStandardConditionPushToggle}
          >
            <span
              aria-hidden="true"
              className={cn(
                'pointer-events-none inline-block h-3 w-3 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                standardConditionEnabled ? 'translate-x-3' : 'translate-x-0',
              )}
            />
          </button>
        </div>
        {standardConditionEnabled && (
          <div className="ml-3 flex flex-col gap-1 border-l-2 border-blue-200 pl-3">
            <label
              htmlFor="admin-standard-condition-type"
              className="block text-xs font-medium text-gray-700"
            >
              Standard Condition Type
            </label>
            <select
              id="admin-standard-condition-type"
              value={definition.externalConditionType ?? ''}
              onChange={onStandardConditionTypeChange}
              className="block w-full rounded border border-gray-300 py-1 pl-2 pr-6 text-gray-700 shadow-sm outline-none focus:ring-0 sm:text-xs"
            >
              {STANDARD_CONDITION_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            {isStandardConditionIncomplete && (
              <p className="text-xs text-red-600">
                Standard condition type is required.
              </p>
            )}
          </div>
        )}
      </div>
    </>
  );
}

const RuleEditor = ({ onCancel, onSave }: RuleFormModalProps) => {
  const { currentChecklistRules: rule, existingRules } = useChecklistStore();
  const [draft, setDraft] = useState<RuleRow | null>(rule);
  const [validExpression, setValidExpression] = useState<boolean>(true);
  const [isTestable, setIsTestable] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<RuleTab>('general');
  const [rulComposerCursorPos, setRulComposerCursorPos] = useState(0);
  const rulComposerRef = useRef<HTMLTextAreaElement>(null);
  const [history, setHistory] = useState<string[]>(['']);
  const [historyIndex, setHistoryIndex] = useState(0);

  const externalFieldQuery = useExternalFields(ExternalFieldType.ENCOMPASS);
  const tenantFieldQuery = useDocumentTypes({
    getAllDocumentTypes: false,
    getFields: true,
  });
  // Rich Encompass field schema (writable + enum options) for the Update LOS
  // Field action — matches the loan-level rule builder exactly.
  const encompassSchemaQuery = useEncompassFieldSchema();
  const enhancedConditionEnabled =
    draft?.definition.externalEnhancedConditionPushOnFailure ?? false;
  const standardConditionEnabled = draft
    ? isStandardConditionRule(draft.definition)
    : false;
  const conditionPushEnabled =
    enhancedConditionEnabled || standardConditionEnabled;
  const conditionTypesQuery = useConditionTypesQuery(enhancedConditionEnabled);
  const conditionTypeId = draft?.definition.conditionTypeId;
  const priorToDefinitionsQuery = usePriorToDefinitionsQuery(
    conditionTypeId ?? '',
    enhancedConditionEnabled && !!conditionTypeId,
  );

  const hasRebuiltExpression = useRef(false);

  const syncRuleBuilderData = (expr: string) => {
    const documentTypes = tenantFieldQuery.data?.documentTypes;
    if (!documentTypes || documentTypes.length === 0) {
      return {
        ruleBuilderJson: draft?.definition?.ruleBuilderJson || [],
        nameExpression: draft?.definition?.nameExpression || '',
      };
    }
    const json = buildRuleBuilderJson(expr, documentTypes);
    const nameExpr = json.length > 0 ? buildNameExpression(expr, json) : '';
    return { ruleBuilderJson: json, nameExpression: nameExpr };
  };

  useEffect(() => {
    setDraft(rule);
    const nextExpression = rule?.definition?.expression ?? '';
    setValidExpression(
      nextExpression.length > 0 ? IsValidRule(nextExpression) : false,
    );
    setIsTestable(extractBracketedValues(nextExpression).length > 0);
    setHistory(['', nextExpression]);
    setHistoryIndex(1);
    hasRebuiltExpression.current = false;
  }, [rule]);

  // Rebuild expression with latest display names when doc types are loaded
  useEffect(() => {
    if (hasRebuiltExpression.current || !draft) return;
    const expression = draft.definition?.expression ?? '';
    if (!expression) return;
    const documentTypes = tenantFieldQuery.data?.documentTypes;
    if (!documentTypes || documentTypes.length === 0) return;

    const existingJson = draft.definition.ruleBuilderJson;
    let finalExpression = expression;
    let json = existingJson || [];
    let nameExpr = draft.definition.nameExpression || '';

    if (existingJson && existingJson.length > 0) {
      const rebuilt = rebuildExpressionWithLatestDisplayNames(
        finalExpression,
        existingJson,
        documentTypes,
      );
      finalExpression = rebuilt.expression;
      json = rebuilt.updatedJson;
    } else if (finalExpression) {
      json = buildRuleBuilderJson(finalExpression, documentTypes);
    }

    if (json.length > 0) {
      nameExpr = buildNameExpression(finalExpression, json);
    }

    hasRebuiltExpression.current = true;

    if (finalExpression !== expression || json !== existingJson) {
      setDraft((prev) =>
        prev
          ? {
              ...prev,
              definition: {
                ...prev.definition,
                expression: finalExpression,
                ruleBuilderJson: json,
                nameExpression: nameExpr,
              },
            }
          : prev,
      );
      setHistory(['', finalExpression]);
      setHistoryIndex(1);
    }
  }, [tenantFieldQuery.data?.documentTypes, draft?.definition?.expression]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!draft) {
    return null;
  }

  const handleDefinitionChange = (
    updates: Partial<LoanChecklistRuleDefinition>,
  ) => {
    setDraft((prev) =>
      prev
        ? {
            ...prev,
            definition: {
              ...prev.definition,
              ...updates,
            },
          }
        : prev,
    );
  };

  const commitExpression = (newExpression: string) => {
    const { ruleBuilderJson, nameExpression } =
      syncRuleBuilderData(newExpression);
    handleDefinitionChange({
      expression: newExpression,
      ruleBuilderJson,
      nameExpression,
    });
    setValidExpression(
      newExpression.length > 0 ? IsValidRule(newExpression) : false,
    );
    setIsTestable(extractBracketedValues(newExpression).length > 0);
  };

  const handleHistory = (value: string) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(value);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  const handleComposerCursorPosition = (newExpression: string) => {
    commitExpression(newExpression);

    const textarea = rulComposerRef.current;
    if (!textarea) return;
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newExpression.length, newExpression.length);
      setRulComposerCursorPos(newExpression.length);
    }, 0);
  };

  const updateRule = (
    type:
      | 'operator'
      | 'docField'
      | 'losField'
      | 'tableColumn'
      | 'date'
      | 'text'
      | 'UNDO'
      | 'REDO',
    value: string,
  ) => {
    const currentExpression = draft.definition.expression ?? '';

    if (type === 'UNDO') {
      if (historyIndex > 0) {
        const newIndex = historyIndex - 1;
        const newExpression = history[newIndex] ?? '';
        setHistoryIndex(newIndex);
        handleComposerCursorPosition(newExpression);
      }
      return;
    }

    if (type === 'REDO') {
      if (historyIndex < history.length - 1) {
        const newIndex = historyIndex + 1;
        const newExpression = history[newIndex] ?? '';
        setHistoryIndex(newIndex);
        handleComposerCursorPosition(newExpression);
      }
      return;
    }

    const textarea = rulComposerRef.current;
    if (!textarea) return;

    const start = rulComposerCursorPos;
    const end = textarea.selectionEnd ?? start;
    const newValue =
      currentExpression.substring(0, start) +
      value +
      currentExpression.substring(end);

    commitExpression(newValue);

    // Update cursor after insert
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + value.length, start + value.length);
      setRulComposerCursorPos(start + value.length);
    }, 0);

    handleHistory(newValue);
  };

  const handleFieldSelect = (
    type: 'docField' | 'losField' | 'tableColumn',
    item: string,
    definition?: string,
  ) => {
    let finalItem = item;

    const textarea = rulComposerRef.current;
    if (textarea && type === 'docField' && draft.definition.expression) {
      const start = rulComposerCursorPos;
      const textBeforeCursor = draft.definition.expression.substring(0, start);

      if (/checkall\s*\([^)]*$/i.test(textBeforeCursor)) {
        const parts = item.split('.');
        if (parts.length > 1) {
          finalItem = parts.slice(1).join('.');
        }
      }
    }

    if (type === 'tableColumn') {
      const def = definition || '#sum#';
      updateRule(type, '[' + finalItem + '.' + def + ']');
    } else {
      updateRule(type, '[' + finalItem + ']');
    }
    setIsTestable(true);
  };

  const handleRuleComposerCurorChange = (
    e:
      | React.MouseEvent<HTMLTextAreaElement>
      | React.KeyboardEvent<HTMLTextAreaElement>,
  ) => {
    setRulComposerCursorPos(e.currentTarget.selectionStart);
  };

  const userOnChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const next = event.target.value;
    commitExpression(next);
    handleHistory(next);
  };

  const isDuplicateName = (name: string) => {
    const normalized = name.trim().toLowerCase();
    if (!normalized) return false;
    return existingRules.some((r) => {
      if (r.localId === draft.localId) return false;
      return r.name.trim().toLowerCase() === normalized;
    });
  };

  const isEnhancedConditionIncomplete =
    enhancedConditionEnabled &&
    (!draft.definition.conditionTypeId || !draft.definition.priorTo);
  const isStandardConditionIncomplete =
    standardConditionEnabled && !draft.definition.externalConditionType;
  const isEncompassFieldTriggerIncomplete =
    draft.definition.encompassFieldTrigger?.enabled &&
    !hasValidEncompassFieldConditions(
      draft.definition.encompassFieldTrigger.conditions,
    );

  const handleConditionTypeChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const conditionType = conditionTypesQuery.data?.find(
      (type) => type.id === event.target.value,
    );
    handleDefinitionChange({
      conditionTypeId: conditionType?.id,
      conditionTypeTitle: conditionType?.title,
      priorTo: undefined,
      source: 'MOS',
    });
  };

  const handleEnhancedConditionPushToggle = () => {
    const nextEnabled = !enhancedConditionEnabled;
    handleDefinitionChange({
      externalEnhancedConditionPushOnFailure: nextEnabled,
      externalConditionPushOnFailure: false,
      ...(nextEnabled
        ? getEnabledEnhancedConditionDefaults()
        : { source: undefined }),
    });
  };

  const handleStandardConditionPushToggle = () => {
    const nextEnabled = !standardConditionEnabled;
    const nextConditionType =
      draft.definition.externalConditionType &&
      draft.definition.externalConditionType !== 'enhanced'
        ? draft.definition.externalConditionType
        : 'underwriting';
    handleDefinitionChange({
      externalEnhancedConditionPushOnFailure: false,
      externalConditionPushOnFailure: nextEnabled,
      ...(nextEnabled
        ? {
            ...getEnabledStandardConditionDefaults(),
            externalConditionType: nextConditionType,
            priorTo: undefined,
            conditionTypeId: undefined,
            conditionTypeTitle: undefined,
            source: undefined,
          }
        : {}),
    });
  };

  const handlePriorToChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    handleDefinitionChange({
      priorTo: event.target.value || undefined,
      source: enhancedConditionEnabled ? 'MOS' : undefined,
    });
  };

  const handleStandardConditionTypeChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const nextConditionType = event.target
      .value as (typeof STANDARD_CONDITION_TYPES)[number]['value'];
    const previousConditionType =
      draft.definition.externalConditionType !== 'enhanced'
        ? draft.definition.externalConditionType
        : undefined;

    handleDefinitionChange({
      externalConditionPushOnFailure: true,
      externalConditionType: nextConditionType,
      standardCondition: withPendingStandardConditionRemoval(
        draft.definition.standardCondition,
        previousConditionType,
        nextConditionType,
      ),
      conditionTypeId: undefined,
      conditionTypeTitle: undefined,
      source: undefined,
    });
  };

  const finalizeAndSave = (action: 'save' | 'publish') => {
    const losError = getUpdateLosFieldError(draft.definition.updateLosField);
    if (losError) {
      toast.error(losError);
      return;
    }
    if (isEncompassFieldTriggerIncomplete) {
      toast.error(
        'Add an Encompass field and value for every enabled trigger condition.',
      );
      return;
    }

    const trimmedName = draft.name.trim();
    const trimmedDescription = draft.description.trim();

    onSave({
      ...draft,
      name: trimmedName || 'Untitled rule',
      description: trimmedDescription,
      definition: {
        ...draft.definition,
        expression: (draft.definition.expression ?? '').trim(),
        active:
          action === 'publish' ? true : (draft.definition.active ?? false),
        draftRule: false,
        source: draft.definition.externalEnhancedConditionPushOnFailure
          ? 'MOS'
          : undefined,
      },
    });
  };

  const enabledActionsCount = [
    draft.definition.loanTaskOnFailure,
    conditionPushEnabled,
    draft.definition.updateLosField?.enabled,
    draft.definition.encompassFieldTrigger?.enabled,
  ].filter(Boolean).length;

  const tabs = [
    { id: 'general' as const, label: 'General', icon: SlidersHorizontal },
    { id: 'alerts' as const, label: 'Alerts', icon: Bell },
    {
      id: 'actions' as const,
      label: 'Actions',
      icon: Zap,
      badge: enabledActionsCount,
    },
  ];

  return (
    <div className="flex flex-col h-[calc(100vh-120px)] w-full relative z-20">
      {/* Header Actions */}
      <div className="  px-4 py-3 flex items-center justify-between  shrink-0 z-20">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            {rule?.id ? 'Edit Rule' : 'Add Rule'}
          </h3>
          <p className="text-sm text-gray-500">
            Use the builder to compose a valid expression and alerts.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <TrueToggle
            label="Active"
            checked={draft.definition.active}
            onChange={(checked) => handleDefinitionChange({ active: checked })}
            variant={draft.definition.active ? 'success' : 'default'}
            containerClassName="rounded border border-gray-200 bg-white px-2 py-1 shadow-sm h-[32px] flex items-center"
          />
          <Button
            variant="outline"
            className="px-3 py-1.5 text-sm text-gray-700 items-center hover:border-gray-400 h-[32px]"
            disabled={
              !validExpression ||
              (draft?.name ?? '').trim() === '' ||
              isDuplicateName(draft?.name ?? '') ||
              isEnhancedConditionIncomplete ||
              isEncompassFieldTriggerIncomplete
            }
            onClick={() => finalizeAndSave('save')}
          >
            Save Draft
          </Button>
          <Button
            variant="contained"
            className="px-3 py-1.5 text-sm text-white items-center h-[32px]"
            disabled={
              !validExpression ||
              (draft?.name ?? '').trim() === '' ||
              isDuplicateName(draft?.name ?? '') ||
              isEnhancedConditionIncomplete ||
              isEncompassFieldTriggerIncomplete
            }
            onClick={() => finalizeAndSave('publish')}
          >
            Save & Publish
          </Button>
          <Button
            variant="outline"
            className="px-2 py-1.5 text-sm items-center hover:border-gray-400 h-[32px]"
            onClick={onCancel}
          >
            <XMarkIcon className="w-4 h-4 text-gray-500" title="Cancel" />
          </Button>
        </div>
      </div>

      <div className="flex-1  ">
        <div className="h-full flex flex-col lg:flex-row gap-2 items-start">
          <div className="w-full lg:w-[32%] h-full pl-2 pt-2 pb-12">
            <div className="bg-white ring-1 ring-gray-200 shadow-sm rounded-md p-4 lg:p-6 flex flex-col flex-1 min-h-[700px] lg:min-h-full">
              <div className="flex flex-col h-full min-h-0">
                {/* Rule status — kept above the segmented tab bar */}
                <div className="flex items-center justify-between shrink-0 mb-3">
                  <span className="text-xs font-medium text-gray-500">
                    Rule status
                  </span>
                  <Badge
                    variant={draft.definition.active ? 'success' : 'warning'}
                    text={draft.definition.active ? 'Published' : 'Draft'}
                  />
                </div>

                {/* Segmented tab control — shared with the loan rule builder */}
                <RuleSettingsTabs
                  tabs={tabs}
                  activeTab={activeTab}
                  onChange={setActiveTab}
                />

                <div className="mt-4 flex-1 min-h-0 overflow-y-auto custom-scrollbar pr-0.5">
                  {/* General Settings */}
                  {activeTab === 'general' && (
                    <div
                      role="tabpanel"
                      id="rule-panel-general"
                      aria-labelledby="rule-tab-general"
                      className="grid grid-cols-2 gap-3"
                    >
                      <div className="col-span-2 flex flex-col gap-1">
                        <label
                          htmlFor="rule-name"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Rule Name
                        </label>
                        <input
                          id="rule-name"
                          name="rule-name"
                          type="text"
                          placeholder="Enter rule name"
                          value={draft.name ?? ''}
                          onChange={(event) =>
                            setDraft((prev) =>
                              prev
                                ? { ...prev, name: event.target.value }
                                : prev,
                            )
                          }
                          className={cn(
                            'block min-w-full rounded py-2 px-2 text-gray-700 shadow-sm border-gray-300 border focus:ring-0 sm:text-xs outline-none',
                            {
                              'border-red-300 outline-red-300 focus:outline-red-500':
                                isDuplicateName(draft.name ?? ''),
                            },
                          )}
                        />
                        {isDuplicateName(draft.name ?? '') && (
                          <p className="mt-1 text-xs text-red-600">
                            Name duplicate.
                          </p>
                        )}
                      </div>

                      <div className="col-span-2 flex flex-col gap-1">
                        <label
                          htmlFor="rule-description"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Description
                        </label>
                        <textarea
                          id="rule-description"
                          name="rule-description"
                          rows={4}
                          style={{ resize: 'none' }}
                          placeholder="Provide a detailed description of the rule"
                          value={draft.description ?? ''}
                          onChange={(event) =>
                            setDraft((prev) =>
                              prev
                                ? { ...prev, description: event.target.value }
                                : prev,
                            )
                          }
                          className="block min-w-full rounded py-2 px-2 text-gray-700 shadow-sm border-gray-300 border focus:ring-0 sm:text-xs outline-none resize-none"
                        />
                      </div>

                      <div className="col-span-2 flex flex-col gap-1">
                        <label
                          htmlFor="rule-borrower-pair"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Borrower
                        </label>
                        <select
                          id="rule-borrower-pair"
                          name="rule-borrower-pair"
                          value={draft.definition.borrowerPairId ?? 'COMMON'}
                          onChange={(event) =>
                            handleDefinitionChange({
                              borrowerPairId: event.target.value,
                            })
                          }
                          className="block min-w-full rounded py-2 pl-2 pr-6 text-gray-700 shadow-sm border-gray-300 border focus:ring-0 sm:text-xs outline-none text-ellipsis"
                        >
                          <option value="COMMON">Common</option>
                          <option value="">Any Borrower</option>
                          <option value="1">Borrower Pair 1</option>
                          <option value="2">Borrower Pair 2</option>
                          <option value="3">Borrower Pair 3</option>
                          <option value="4">Borrower Pair 4</option>
                          <option value="5">Borrower Pair 5</option>
                          <option value="6">Borrower Pair 6</option>
                        </select>
                      </div>
                    </div>
                  )}

                  {/* Evaluation Alerts */}
                  {activeTab === 'alerts' && (
                    <div
                      role="tabpanel"
                      id="rule-panel-alerts"
                      aria-labelledby="rule-tab-alerts"
                      className="flex flex-col gap-3"
                    >
                      <p className="text-[11px] text-gray-400">
                        Messages shown to users when this rule is evaluated.
                      </p>
                      <div className="grid grid-cols gap-3">
                        <div className="flex flex-col gap-1">
                          <label
                            htmlFor="rule-true-constraint"
                            className="block text-xs font-medium text-gray-700"
                          >
                            If Passed
                          </label>
                          <textarea
                            id="rule-true-constraint"
                            value={draft.definition.trueConstraint ?? ''}
                            onChange={(e) =>
                              handleDefinitionChange({
                                trueConstraint: e.target.value,
                              })
                            }
                            rows={4}
                            style={{ resize: 'none' }}
                            placeholder="Message on success..."
                            className="block min-w-full rounded py-1 px-2 text-green-700 shadow-sm border-green-300 border focus:ring-0 sm:text-xs bg-green-50/50 outline-none"
                          />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label
                            htmlFor="rule-false-constraint"
                            className="block text-xs font-medium text-gray-700"
                          >
                            If Failed
                          </label>
                          <textarea
                            id="rule-false-constraint"
                            value={draft.definition.falseConstraint ?? ''}
                            onChange={(e) =>
                              handleDefinitionChange({
                                falseConstraint: e.target.value,
                              })
                            }
                            rows={4}
                            style={{ resize: 'none' }}
                            placeholder="Message on failure..."
                            className="block min-w-full rounded py-1 px-2 text-red-700 shadow-sm border-red-300 border focus:ring-0 sm:text-xs bg-red-50/50 outline-none"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Automated Actions */}
                  {activeTab === 'actions' && (
                    <div
                      role="tabpanel"
                      id="rule-panel-actions"
                      aria-labelledby="rule-tab-actions"
                      className="flex flex-col gap-2"
                    >
                      <p className="text-[11px] text-gray-400">
                        Run automatically when the rule fails or passes.
                      </p>
                      <div
                        className={cn(
                          'flex flex-row items-center justify-between rounded border p-2.5',
                          draft.definition.loanTaskOnFailure
                            ? 'border-blue-200 bg-blue-50/30'
                            : 'border-gray-200',
                        )}
                      >
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-gray-700">
                            Loan Task Creation
                          </span>
                          <span className="text-[10px] text-gray-500">
                            Generate on failure
                          </span>
                        </div>
                        <button
                          type="button"
                          role="switch"
                          aria-label="Loan Task Creation on failure"
                          aria-checked={
                            draft.definition.loanTaskOnFailure ?? false
                          }
                          className={cn(
                            'relative inline-flex h-4 w-7 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none',
                            draft.definition.loanTaskOnFailure
                              ? 'bg-blue-600'
                              : 'bg-gray-200',
                          )}
                          onClick={() =>
                            handleDefinitionChange({
                              loanTaskOnFailure: !(
                                draft.definition.loanTaskOnFailure ?? false
                              ),
                            })
                          }
                        >
                          <span
                            aria-hidden="true"
                            className={cn(
                              'pointer-events-none inline-block h-3 w-3 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                              draft.definition.loanTaskOnFailure
                                ? 'translate-x-3'
                                : 'translate-x-0',
                            )}
                          />
                        </button>
                      </div>

                      <RuleConditionPushSettings
                        definition={draft.definition}
                        standardConditionEnabled={standardConditionEnabled}
                        enhancedConditionEnabled={enhancedConditionEnabled}
                        conditionTypeId={conditionTypeId}
                        conditionTypes={conditionTypesQuery.data ?? []}
                        priorToOptions={priorToDefinitionsQuery.data ?? []}
                        isPriorToLoading={priorToDefinitionsQuery.isLoading}
                        isPriorToError={priorToDefinitionsQuery.isError}
                        isEnhancedConditionIncomplete={
                          isEnhancedConditionIncomplete
                        }
                        isStandardConditionIncomplete={
                          isStandardConditionIncomplete
                        }
                        onEnhancedConditionPushToggle={
                          handleEnhancedConditionPushToggle
                        }
                        onStandardConditionPushToggle={
                          handleStandardConditionPushToggle
                        }
                        onConditionTypeChange={handleConditionTypeChange}
                        onStandardConditionTypeChange={
                          handleStandardConditionTypeChange
                        }
                        onPriorToChange={handlePriorToChange}
                      />

                      <UpdateLosFieldEditor
                        config={draft.definition.updateLosField}
                        onChange={(next) =>
                          handleDefinitionChange({ updateLosField: next })
                        }
                        encompassFields={
                          encompassSchemaQuery.data?.fields ?? []
                        }
                        isFieldsLoading={encompassSchemaQuery.isLoading}
                        isFieldsError={encompassSchemaQuery.isError}
                        documentTypes={
                          tenantFieldQuery.data?.documentTypes ?? []
                        }
                      />
                      <EncompassFieldTriggerEditor
                        config={draft.definition.encompassFieldTrigger}
                        onChange={(next) =>
                          handleDefinitionChange({
                            encompassFieldTrigger: next,
                          })
                        }
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="w-full lg:w-[68%] h-full overflow-y-auto custom-scrollbar pr-2 pt-2 pb-12">
            <div className="bg-white w-full ring-1 ring-gray-200 shadow-sm rounded-md p-3 flex flex-col flex-1 h-full min-h-[600px] lg:min-h-0">
              <RuleComposer
                handleFieldSelect={handleFieldSelect}
                externalFieldQuery={externalFieldQuery}
                sharedFieldQuery={tenantFieldQuery}
                updateRule={updateRule}
                validExpression={validExpression}
                userOnChange={userOnChange}
                handleRuleComposerCurorChange={handleRuleComposerCurorChange}
                rulComposerRef={rulComposerRef}
                isTestable={isTestable}
                openTestDialog={() => {}}
                showTestButton={false}
                expression={draft.definition.expression ?? ''}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const RuleManagement = () => {
  const {
    currentChecklist,
    actions,
    currentChecklistRules: editingRule,
  } = useChecklistStore();
  const params = useParams();
  const checklistId = params.checklistId ?? '';
  const tenantChecklist = useTenantChecklistById(checklistId);
  const [rules, setRules] = useState<RuleRow[]>([]);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const updateRuleByIdMutation = useRuleUpdateByIdMutation(checklistId);
  const saveTenantChecklistMutation = useSaveTenantChecklistMutation();
  const [isGlobalConfigOpen, setIsGlobalConfigOpen] = useState(false);
  const [draftConditionMode, setDraftConditionMode] = useState<
    'all' | 'conditional'
  >('all');
  const [draftConditions, setDraftConditions] = useState<
    LoanChecklistRuleCondition[]
  >([]);

  const setChecklistMetadata = useCallback(
    (
      updates: Partial<{
        conditions: LoanChecklistRuleCondition[];
        conditionMode: 'all' | 'conditional';
      }>,
    ) => {
      // Use latest store state to avoid stale overwrites when multiple updates happen quickly.
      const prevChecklist = useChecklistStore.getState().currentChecklist;
      actions.setCurrentChecklist({
        ...prevChecklist,
        metadata: {
          ...prevChecklist.metadata,
          ...updates,
        },
      });
    },
    [actions],
  );

  const handleSaveChecklist = useCallback(() => {
    saveTenantChecklistMutation.mutate(
      {
        tenantChecklistId: currentChecklist.id ?? '',
        name: currentChecklist.name ?? '',
        metadata: currentChecklist.metadata ?? {},
        batchCreateRules: currentChecklist.rules ?? [],
        active: currentChecklist.active ?? true,
        isGlobal: currentChecklist.isGlobal ?? false,
        assignedFromLoanId: currentChecklist.assignedFromLoanId ?? null,
        lastModifiedUserId: currentChecklist.lastModifiedUserId ?? null,
        displayOrder: currentChecklist.displayOrder ?? null,
      },
      {
        onSuccess: () => {
          toast.success('Checklist saved successfully');
        },
        onError: (error) => {
          toast.error(error.message ?? 'Failed to save checklist');
        },
      },
    );
  }, [currentChecklist, saveTenantChecklistMutation]);

  const setConditionMode = useCallback(
    (mode: 'all' | 'conditional') =>
      setChecklistMetadata({ conditionMode: mode }),
    [setChecklistMetadata],
  );

  const conditionMode: 'all' | 'conditional' = useMemo(() => {
    const globalEnabled = Boolean(currentChecklist.isGlobal);
    if (!globalEnabled) return 'all';
    const explicit = currentChecklist.metadata?.conditionMode;
    if (explicit === 'all' || explicit === 'conditional') return explicit;
    const count = currentChecklist.metadata?.conditions?.length ?? 0;
    return count > 0 ? 'conditional' : 'all';
  }, [
    currentChecklist.isGlobal,
    currentChecklist.metadata?.conditionMode,
    currentChecklist.metadata?.conditions,
  ]);

  useEffect(() => {
    if (!isGlobalConfigOpen) return;
    setDraftConditionMode(conditionMode);
    setDraftConditions(currentChecklist.metadata?.conditions ?? []);
  }, [
    conditionMode,
    currentChecklist.metadata?.conditions,
    isGlobalConfigOpen,
  ]);

  useEffect(() => {
    if (tenantChecklist.data && tenantChecklist.isSuccess) {
      const orderedRules = normalizeRulesBySequence(tenantChecklist.data.rules);
      setRules(orderedRules.map((rule, index) => buildRuleRow(rule, index)));
      actions.setCurrentChecklist({
        ...tenantChecklist.data,
        rules: orderedRules,
      });
    }
  }, [tenantChecklist.data, tenantChecklist.isSuccess, checklistId, actions]);

  const filteredRules = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return sortRulesBySequence(rules);
    return sortRulesBySequence(
      rules.filter((rule) => rule.name.toLowerCase().includes(term)),
    );
  }, [rules, search]);

  const handleEditRule = useCallback(
    (rule: RuleRow) => {
      actions.setCurrentChecklistRules(rule);
      actions.setExistingRules(rules);
    },
    [actions, rules],
  );

  const columnDefs = useMemo<ColDef<RuleRow>[]>(() => {
    return [
      {
        headerName: 'Order',
        colId: 'ruleSequenceNo',
        rowDrag: true,
        width: 110,
        minWidth: 90,
        flex: 0,
        sortable: false,
        filter: false,
        valueGetter: (params) =>
          params.data?.definition.ruleSequenceNo ??
          (params.node ? (params.node.rowIndex ?? 0) + 1 : ''),
      },
      {
        headerName: 'Name',
        field: 'name',
        flex: 1.2,
        valueGetter: (params) => params.data?.name || 'Untitled rule',
      },
      {
        headerName: 'Description',
        field: 'description',
        flex: 1.6,
        valueGetter: (params) => params.data?.description || '-',
      },
      {
        headerName: 'Expression',
        flex: 2,
        cellRenderer: RuleExpressionRenderer,
      },
      {
        headerName: 'Action',
        field: 'localId',
        width: 120,
        pinned: 'right',
        sortable: false,
        filter: false,
        cellRenderer: RuleActionRenderer,
        cellRendererParams: {
          onEdit: handleEditRule,
        },
      },
    ];
  }, [handleEditRule]);

  const defaultColDef = useMemo(() => {
    return {
      resizable: true,
      sortable: false,
      filter: false,
      suppressMovable: true,
    };
  }, []);

  const handleOpenAddRule = useCallback(() => {
    const newRule = createNewRule(rules.length + 1);
    const nextSequenceNo =
      Math.max(0, ...rules.map((r) => r.definition.ruleSequenceNo || 0)) + 1;
    newRule.definition = {
      ...newRule.definition,
      ruleSequenceNo: nextSequenceNo,
      active: false,
      draftRule: true,
    };
    actions.setCurrentChecklistRules(newRule);
  }, [rules, actions]);

  const handleSaveRule = (rule: RuleRow) => {
    const isEdit = Boolean(rule.id && rule.id !== '');
    updateRuleByIdMutation.mutate(
      {
        ruleId: rule.id ?? '',
        checklistId: currentChecklist.id ?? '',
        name: rule.name,
        description: rule.description,
        definition: rule.definition,
      },
      {
        onSuccess: (data) => {
          if (!data.success) {
            toast.error(data.message ?? 'Failed to update rule');
            return;
          }
          toast.success(
            isEdit ? 'Rule updated successfully' : 'Rule created successfully',
          );

          actions.setCurrentChecklistRules(null);
          const existingIndex = rules.findIndex(
            (r) => r.localId === rule.localId,
          );
          if (existingIndex !== -1 && isEdit) {
            const newRules = [...rules];
            newRules[existingIndex] = rule;
            const sequencedRules = normalizeRulesBySequence(newRules);
            setRules(sequencedRules.map((r, index) => buildRuleRow(r, index)));
            actions.setCurrentChecklist({
              ...currentChecklist,
              rules: sequencedRules,
            });
            actions.setExistingRules(sequencedRules);
          } else {
            const newRules = normalizeRulesBySequence([...rules, rule]);
            setRules(newRules.map((r, index) => buildRuleRow(r, index)));
            actions.setCurrentChecklist({
              ...currentChecklist,
              rules: newRules,
            });
            actions.setExistingRules(newRules);
          }
        },
        onError: (error) => {
          toast.error(error.message ?? 'Failed to update rule');
        },
      },
    );
  };

  const handleRuleRowDragEnd = useCallback(
    async (event: RowDragEndEvent<RuleRow>) => {
      const visibleRows: RuleRow[] = [];
      event.api.forEachNodeAfterFilterAndSort((node) => {
        if (node.data) visibleRows.push(node.data);
      });

      if (visibleRows.length === 0) return;

      const visibleIds = new Set(visibleRows.map((rule) => rule.localId));
      const orderedVisible = [...visibleRows];
      const nextRules = resequenceRules(
        rules.map((rule) =>
          visibleIds.has(rule.localId)
            ? (orderedVisible.shift() ?? rule)
            : rule,
        ),
      );

      setRules(nextRules.map((rule, index) => buildRuleRow(rule, index)));
      actions.setCurrentChecklist({
        ...currentChecklist,
        rules: nextRules,
      });
      actions.setExistingRules(nextRules);

      try {
        await saveTenantChecklistMutation.mutateAsync({
          tenantChecklistId: currentChecklist.id ?? '',
          name: currentChecklist.name ?? '',
          metadata: currentChecklist.metadata ?? {},
          batchCreateRules: nextRules,
          active: currentChecklist.active ?? true,
          isGlobal: currentChecklist.isGlobal ?? false,
          assignedFromLoanId: currentChecklist.assignedFromLoanId ?? null,
          lastModifiedUserId: currentChecklist.lastModifiedUserId ?? null,
          displayOrder: currentChecklist.displayOrder ?? null,
        });
        toast.success('Rule order saved');
      } catch (error) {
        toast.error(
          error instanceof Error ? error.message : 'Failed to save rule order',
        );
        void tenantChecklist.refetch();
      }
    },
    [
      actions,
      currentChecklist,
      rules,
      saveTenantChecklistMutation,
      tenantChecklist,
    ],
  );

  const handleSaveGlobalConfig = () => {
    if (
      draftConditionMode === 'conditional' &&
      !hasValidEncompassFieldConditions(draftConditions)
    ) {
      toast.error(
        'Please add at least one condition to run the checklist based on conditions.',
      );
      return;
    }
    setChecklistMetadata({
      conditionMode: draftConditionMode,
      conditions: draftConditionMode === 'conditional' ? draftConditions : [],
    });
    setIsGlobalConfigOpen(false);
  };

  const handleCancelEdit = () => actions.setCurrentChecklistRules(null);

  return (
    <div className={configContainerClassName}>
      {!editingRule && (
        <>
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <h2 className="text-2xl font-semibold text-gray-900">
                  {currentChecklist?.name || 'Checklist Rules'}
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Manage and review the rules attached to this checklist.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="contained"
                  onClick={() => navigate('/admin/checklist-management')}
                >
                  <XMarkIcon className="size-4" title="Close" />
                </Button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            <div
              className={cn(configCardClassName, 'lg:col-span-2 p-4 sm:p-6')}
            >
              <div className="flex flex-row items-center justify-between gap-1">
                <div className="flex flex-col gap-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Checklist settings
                  </p>
                  <p className="text-sm text-gray-600">
                    Details and eligibility conditions for this checklist.
                  </p>
                </div>
                <Button
                  variant="contained"
                  disabled={saveTenantChecklistMutation.isPending}
                  onClick={handleSaveChecklist}
                >
                  Save
                </Button>
              </div>

              <div className="mt-5 space-y-5 max-h-[calc(100vh-240px)] overflow-auto pr-1">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="checklist-name"
                      className="text-sm font-medium text-gray-900"
                    >
                      Name
                    </label>
                    <input
                      id="checklist-name"
                      className={`${adminInputClassName} bg-white`}
                      placeholder="Enter checklist name"
                      value={currentChecklist.name ?? ''}
                      onChange={(event) =>
                        actions.setCurrentChecklist({
                          ...currentChecklist,
                          name: event.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="checklist-category"
                      className="text-sm font-medium text-gray-900"
                    >
                      Category
                    </label>
                    <input
                      id="checklist-category"
                      className={`${adminInputClassName} bg-white`}
                      placeholder="Enter checklist category"
                      value={currentChecklist.metadata?.category ?? ''}
                      onChange={(event) =>
                        actions.setCurrentChecklist({
                          ...currentChecklist,
                          metadata: {
                            ...currentChecklist.metadata,
                            category: event.target.value,
                          },
                        })
                      }
                    />
                  </div>
                  <div className="sm:col-span-2 flex flex-col gap-2">
                    <label
                      htmlFor="checklist-description"
                      className="text-sm font-medium text-gray-900"
                    >
                      Description
                    </label>
                    <textarea
                      id="checklist-description"
                      className={`${adminInputClassName} bg-white resize-y`}
                      rows={3}
                      placeholder="Enter checklist description"
                      value={currentChecklist.metadata?.description ?? ''}
                      onChange={(event) =>
                        actions.setCurrentChecklist({
                          ...currentChecklist,
                          metadata: {
                            ...currentChecklist.metadata,
                            description: event.target.value,
                          },
                        })
                      }
                    />
                  </div>
                </div>

                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <h3 className="text-sm font-semibold text-gray-900">
                    Status
                  </h3>
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <TrueToggle
                      label="Active"
                      checked={currentChecklist.active ?? false}
                      onChange={(checked) =>
                        actions.setCurrentChecklist({
                          ...currentChecklist,
                          active: checked,
                        })
                      }
                      variant={currentChecklist.active ? 'success' : 'default'}
                      containerClassName="rounded-md border border-gray-200 bg-white px-3 py-2 shadow-sm"
                    />
                    <TrueToggle
                      label="Global"
                      checked={currentChecklist.isGlobal ?? false}
                      onChange={(checked) => {
                        actions.setCurrentChecklist({
                          ...currentChecklist,
                          isGlobal: checked,
                        });
                        if (checked) {
                          // Default to "all" unless user opts into conditions.
                          if (
                            currentChecklist.metadata?.conditionMode !==
                              'all' &&
                            currentChecklist.metadata?.conditionMode !==
                              'conditional'
                          ) {
                            setConditionMode('all');
                          }
                          setIsGlobalConfigOpen(true);
                        } else {
                          setIsGlobalConfigOpen(false);
                        }
                      }}
                      variant={
                        (currentChecklist.isGlobal ?? false)
                          ? 'info'
                          : 'default'
                      }
                      containerClassName="rounded-md border border-gray-200 bg-white px-3 py-2 shadow-sm"
                    />
                  </div>
                </div>
                {(currentChecklist.isGlobal ?? false) && (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900">
                          Global behavior
                        </h3>
                        <p className="text-sm text-gray-600 mt-1">
                          {conditionMode === 'all'
                            ? 'Runs for all new loans.'
                            : `Runs based on ${currentChecklist.metadata?.conditions?.length ?? 0} condition(s).`}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        onClick={() => setIsGlobalConfigOpen(true)}
                      >
                        Configure
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="lg:col-span-3 flex flex-col gap-4">
              {!editingRule && (
                <div className="grid grid-cols-2">
                  <div className="min-w-full sm:max-w-md">
                    <label
                      htmlFor="rule-search"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Search
                    </label>
                    <div className="relative">
                      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                      <input
                        id="rule-search"
                        className={`${adminInputClassName} pl-10 pr-10 bg-white min-w-full`}
                        placeholder="Search rules"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                      />
                      {search.trim() !== '' && (
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                          onClick={() => setSearch('')}
                          aria-label="Clear search"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="flex items-end gap-2 justify-end">
                    <ChecklistSyncButton
                      checklistId={currentChecklist.id ?? ''}
                      checklistName={currentChecklist.name ?? 'this checklist'}
                      isGlobal={Boolean(currentChecklist.isGlobal)}
                    />
                    <Button variant="contained" onClick={handleOpenAddRule}>
                      Add Rule
                    </Button>
                  </div>
                </div>
              )}

              <div className={cn(configCardClassName, 'overflow-hidden')}>
                <div className="h-[412px] w-full">
                  <AGGrid
                    rowData={filteredRules}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    getRowId={(params) => params.data.localId}
                    rowDragManaged={true}
                    onRowDragEnd={handleRuleRowDragEnd}
                  />
                </div>
                <ChecklistSyncStatus
                  checklistId={currentChecklist.id ?? ''}
                  isGlobal={Boolean(currentChecklist.isGlobal)}
                />
              </div>
            </div>
          </div>
        </>
      )}

      <BaseModal
        open={isGlobalConfigOpen}
        onClose={() => setIsGlobalConfigOpen(false)}
        size="3xl"
      >
        <div className="space-y-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Global checklist behavior
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Choose how this checklist runs for new loans. If you select
                conditions, add rules below.
              </p>
            </div>
            <Button variant="outline" onClick={handleSaveGlobalConfig}>
              Done
            </Button>
          </div>

          <div className="space-y-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
            <label className="flex items-start gap-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 cursor-pointer size-full">
              <input
                type="radio"
                name="global-run-mode"
                className="mt-1"
                aria-label="Run checklist by default for all new loans"
                checked={draftConditionMode === 'all'}
                onChange={() => {
                  if (draftConditions.length > 0) {
                    toast.warning(
                      'Existing conditions will be removed when you click Done.',
                    );
                  }
                  setDraftConditionMode('all');
                }}
              />
              <span className="flex flex-col">
                <span className="text-sm font-medium text-gray-900">
                  Default for all new loans
                </span>
                <span className="text-sm text-gray-600">
                  No conditions required.
                </span>
              </span>
            </label>

            <label className="flex items-start gap-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 cursor-pointer size-full">
              <input
                type="radio"
                name="global-run-mode"
                className="mt-1"
                aria-label="Run checklist based on conditions"
                checked={draftConditionMode === 'conditional'}
                onChange={() => {
                  setDraftConditionMode('conditional');
                  if (draftConditions.length === 0) {
                    setDraftConditions([
                      { field: '', operator: 'and', value: '' },
                    ]);
                  }
                }}
              />
              <span className="flex flex-col">
                <span className="text-sm font-medium text-gray-900">
                  Run for new loans that match the conditions
                </span>
                <span className="text-sm text-gray-600">
                  Define one or more conditions to match.
                </span>
              </span>
            </label>
          </div>

          {draftConditionMode === 'conditional' && (
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="mb-4 text-sm font-semibold text-gray-900">
                Conditions
              </h4>
              <EncompassFieldConditionEditor
                conditions={draftConditions}
                onChange={setDraftConditions}
                idPrefix="global-checklist-condition"
              />
            </div>
          )}
        </div>
      </BaseModal>

      {editingRule && (
        <RuleEditor onCancel={handleCancelEdit} onSave={handleSaveRule} />
      )}
    </div>
  );
};
